import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IDS } from "@/lib/testIds";
import { Calculator } from "@phosphor-icons/react";

export default function MealCalculator() {
  const [codes, setCodes] = useState([]);
  const [dietCode, setDietCode] = useState("");
  const [total, setTotal] = useState("");
  const [mode, setMode] = useState("exact");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/meal-codes").then((r) => {
      setCodes(r.data);
      if (r.data.length) setDietCode(r.data[0].diet_code);
    }).catch((err) => {
      if (process.env.NODE_ENV !== "production") console.debug("[meal-calc] load meal-codes failed:", err?.message || err);
    });
  }, []);

  const calc = async () => {
    if (!dietCode || !total) return;
    try {
      const { data } = await api.post("/meal-codes/calculate", {
        diet_code: dietCode,
        required_total: parseInt(total, 10),
        round_mode: mode,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const selectedCode = codes.find((c) => c.diet_code === dietCode);

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <div className="mb-4">
        <div className="label-tech mb-1">Diet & Meals</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight flex items-center gap-2">
          <Calculator size={28} weight="bold" className="text-primary" /> Meal Calculator
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Convert meal totals into optimal packaging combinations.</p>
      </div>

      <div className="card-flat border-border p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="label-tech mb-1">Diet Code</div>
            <select
              data-testid={IDS.mealDietSelect}
              value={dietCode}
              onChange={(e) => setDietCode(e.target.value)}
              className="w-full h-11 px-2 border border-border bg-background rounded-sm text-sm"
            >
              {codes.map((c) => (
                <option key={c.diet_code} value={c.diet_code}>{c.diet_code} — {c.diet_family}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="label-tech mb-1">Total Meals Required</div>
            <input
              data-testid={IDS.mealTotalInput}
              type="number"
              min="1"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              className="w-full h-11 px-3 border border-border bg-background rounded-sm text-lg font-display font-bold outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g. 458"
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-1 mt-3">
          {[
            { k: "exact", label: "Exact" },
            { k: "pack", label: "Round Packs" },
            { k: "half_rack", label: "Half Racks" },
            { k: "full_rack", label: "Full Racks" },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setMode(o.k)}
              className={`h-9 border rounded-sm text-[10px] font-bold uppercase tracking-widest btn-hard ${
                mode === o.k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
              }`}
            >{o.label}</button>
          ))}
        </div>

        <button
          data-testid={IDS.mealCalcSubmit}
          onClick={calc}
          className="btn-hard mt-3 w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white"
        >
          Calculate
        </button>
      </div>

      {selectedCode && (
        <div className="card-flat border-border p-4 mb-4">
          <div className="label-tech mb-2">Conversion (Diet {selectedCode.diet_code})</div>
          <div className="grid grid-cols-3 gap-3 text-sm font-mono">
            <div><div className="text-muted-foreground text-xs">Meals / pack</div><div className="font-display font-black text-xl">{selectedCode.meals_per_pack}</div></div>
            <div><div className="text-muted-foreground text-xs">Meals / half rack</div><div className="font-display font-black text-xl">{selectedCode.meals_per_half_rack}</div></div>
            <div><div className="text-muted-foreground text-xs">Meals / full rack</div><div className="font-display font-black text-xl">{selectedCode.meals_per_full_rack}</div></div>
          </div>
        </div>
      )}

      {result && (
        <div data-testid={IDS.mealCalcResult} className="card-flat border-primary border-2 p-5">
          <div className="label-tech mb-2 text-primary">Recommendation</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><div className="text-xs text-muted-foreground">Full Racks</div><div className="font-display font-black text-3xl">{result.full_racks}</div></div>
            <div><div className="text-xs text-muted-foreground">Half Racks</div><div className="font-display font-black text-3xl">{result.half_racks}</div></div>
            <div><div className="text-xs text-muted-foreground">Packs</div><div className="font-display font-black text-3xl">{result.packs}</div></div>
            <div><div className="text-xs text-muted-foreground">Individuals</div><div className="font-display font-black text-3xl">{result.individuals}</div></div>
          </div>
          <div className="mt-4 pt-4 border-t border-border font-mono text-sm">
            Total meals covered: <span className="font-bold text-primary">{result.total_meals}</span>
          </div>
        </div>
      )}
    </div>
  );
}
